<script setup lang="ts">
import { MetaTitle } from "@/types/Facet";
import { useFetchPrefs } from '@/composables/useFetchCommonAPI'
import { useFetchMasterData } from "@/composables/useFetchMaster";
import { useMasterStore } from '@/stores/masterStore'
import { useRouter } from 'vue-router';

const masterStore = useMasterStore();
const router = useRouter();
const conditionSelectionRef = ref();
const isAllConditionOk = computed(() => conditionSelectionRef.value?.isAllConditionOk ?? false);
const { fetchAllMasters } = useFetchMasterData();

// 都道府県マスタAPI呼び出し
const { fetchPrefs } = await useFetchPrefs(13);
const prefList = computed(() => fetchPrefs.value?.prefList ?? []);

/**
 * 各種マスタ（建物種別・構造・金額など）をstoreから取得
 * 
 * - buildingTypeListはマンション用/戸建て用のリストをマージ＆重複除去
 * - 他リストは種別ごとに単純にstoreから取得
 */
const buildingTypeList = computed(() => {
  const mansion = masterStore.mansion?.buildType ?? []
  const house = masterStore.house?.buildType ?? []
  return [...mansion, ...house].filter(
    (item, idx, arr) =>
      arr.findIndex(x => x.id === item.id) === idx
  )
  .reverse();
})
const mansionBuildingStructureList = computed(() => masterStore.mansion?.buildingStructure ?? []);
const houseBuildingStructureList = computed(() => masterStore.house?.buildingStructure ?? []);
const buildingAgeList = computed(() => masterStore.mansion?.buildingAge ?? []);
const mansionBuildingInsAmountList = computed(() => masterStore.mansion?.buildingInsAmount ?? []);
const houseBuildingInsAmountList = computed(() => masterStore.house?.buildingInsAmount ?? []);
const buildingInsAmountList = computed(() => masterStore.mansion?.buildingInsAmount ?? []);
const mansionHouseholdInsAmountList = computed(() => masterStore.mansion?.householdInsAmount ?? []);
const houseHouseholdInsAmountList = computed(() => masterStore.house?.householdInsAmount ?? []);
const householdInsAmountList = computed(() => masterStore.mansion?.householdInsAmount ?? []);
const seismicYearsDiscountList = computed(() => masterStore.mansion?.seismicYearsDiscount ?? []); 

/**
 * masterDataList: 選択肢として使う各種リストをまとめたオブジェクト
 */
const masterDataList = computed(() => ({
  buildingTypeList: buildingTypeList.value,               // 建物種類リスト
  mansionBuildingStructureList: mansionBuildingStructureList.value, // マンション用建物構造リスト
  houseBuildingStructureList: houseBuildingStructureList.value,     // 戸建て用建物構造リスト
  buildingAgeList: buildingAgeList.value,                 // 年数リスト
  prefList: prefList.value,                               // 都道府県リスト
  mansionBuildingInsAmountList: mansionBuildingInsAmountList.value, // マンション用建物保険金額リスト
  houseBuildingInsAmountList: houseBuildingInsAmountList.value,   // 戸建用建物保険金額リスト
  buildingInsAmountList: buildingInsAmountList.value,     // 建物保険金額リスト
  houseHouseholdInsAmountList: houseHouseholdInsAmountList.value, // 戸建て用家財保険金額リスト
  mansionHouseholdInsAmountList: mansionHouseholdInsAmountList.value, // マンション用
  householdInsAmountList: householdInsAmountList.value,   // 家財保険金額リスト
  seismicYearsDiscountList: seismicYearsDiscountList.value, // 耐震割引リスト
}));

// 確認画面へ遷移
const onConfirmNavigate = async () => {
  // NG項目がある状態で意図的にdisabledを削除し、確認画面へ遷移しないようにする
  if (!isAllConditionOk.value) {
    return;
  }
  await router.push('/compare/confirm');
}

// コンポーネントマウント時にマスターデータを取得してストアへ保存、デフォルト値をストアに保存
onMounted(async() => {
  // セッション期限切れフラグをリセット
  // sessionStorage.removeItem('__session_expired__');

  // マスター取得
  await fetchAllMasters();

  // セッションの有効期限を更新
  // conditionStore.updateLastSavedAt()

  // ローディングオーバーレイを削除
  const loading = document.getElementsByClassName("loading-overlay");
  if (loading[0] !== undefined) {
    loading[0].remove();
  }
});

const metaTitle: MetaTitle = useState("metaTitle");
metaTitle.value = `火災保険料シミュレーション｜入力｜保険スクエアbang! 火災保険`;
useHead({
  bodyAttrs: {
    class: ["top", "compare"],
  },
});
useSeoMeta({
  title: `火災保険料シミュレーション｜入力｜保険スクエアbang! 火災保険`,
  ogTitle: `火災保険料シミュレーション｜入力｜保険スクエアbang! 火災保険`,
  ogUrl: "https://fire.bang.co.jp/compare/",
});

</script>

<template>
  <div>
    <OrganismsCompareIndicator/>
    <div class="wrap-contents">
      <h1>
        <picture>
          <source
            srcset="@/assets/images/compare/verWoman_pc.png"
            media="(min-width: 751px)"
          >
          <img
            src="@/assets/images/compare/verWoman.png"
            alt="火災保険料シミュレーション 保険金額や保障内容を任意に設定して保険料をオンラインでご確認いただけます。"
          >
        </picture>
      </h1>
      <OrganismsCompareConditionSelection
        ref="conditionSelectionRef"
        :master-data-list="masterDataList"
      />
      <OrganismsCompareButton
        :buttonClass="[!isAllConditionOk ? 'is-disabled' : ''].join(' ')"
        :icon="isAllConditionOk ? 'arrow' : ''"
        @click="onConfirmNavigate"
        :disabled="!isAllConditionOk"
        id="compare-btn"
      >
        {{ !isAllConditionOk ? '必須項目のご入力をお願いいたします' : '入力内容を確認する' }}
      </OrganismsCompareButton>
    </div>
    <OrganismsCompareAnnotation/>
    <OrganismsCompareLoading />
  </div>
</template>

<style lang="scss" scoped>
/*---------------------------------------
.breadcrumbs
----------------------------------------*/
.breadcrumbs {
  border-top: 1px solid #dcdcdc;
  padding: 0.8em 4% 1.5em;
  font-size: px(12);
  line-height: 1.27;

  a {
    color: $colorDarkBlue;
  }
}

.wrap-contents {
  padding: 0;
  position: relative;
}

h1 {
  padding: 0.53% 0;
  font-size: px(19.9);
  font-weight: 700;
}

.request-other {
  border-bottom: 1px solid #dcdcdc;
  display: flex;

  li {
    width: 50%;
    position: relative;

    &:first-of-type::after {
      content: '';
      width: 1px;
      height: 15px;
      display: inline-block;
      background-color: #dcdcdc;
      position: absolute;
      right: 0;
      top: 50%;
      transform: translate(0, -50%);
    }

    a {
      padding: 6% 13% 5% 9%;
      display: block;
      color: $colorDarkBlue;
      font-size: px(13);
      font-weight: bold;
      background: url(~/assets/images/compare/arrow_link_sp.png) no-repeat 91%
        center;
      background-size: 3% auto;
    }
  }
}

.request-other li.house a.pc {
  display: none;
}

.commonGuide {
  margin: 5% 4% 3%;
  padding: 3%;
  background-color: #f8f7f3;
  p {
    padding: 2% 0 0;
    border-top: 1px solid #dcdcdc;
    font-size: px(9.9);
    br {
      display: none;
    }
  }
}

.commonGuide-title {
  padding: 0 0 1%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  div {
    font-size: px(13);
    font-weight: bold;
  }

  span {
    font-size: px(9.9);
  }
}

.guideCalculation {
  padding: 5% 4% 21.6%;
}

:deep {
  .guideCalculation-inner {
    padding: 3%;
    background-color: #f8f7f3;
  }

  .guideCalculation-title {
    padding: 0 0 2%;
    border-bottom: 1px solid #dcdcdc;
    font-size: px(13);
    font-weight: bold;
  }

  .guideCalculation-inner {
    ul {
      padding: 1.2% 0 0;
    }

    li {
      margin: 0 0 0 1em;
      font-size: px(9.9);
      text-indent: -1em;

      a {
        color: #148cde;
      }
    }
  }

  .guideCalculation-end {
    padding: 6% 0 0;
    font-size: px(13);
  }
}

.documentNumber {
  padding: 8% 4% 0;
}

.pageTop {
  @include pageTop(sp);
}

@include media(pc) {
  .breadcrumbs {
    margin: 0 auto;
    padding: 11px 0 34px;
    border-top: none;
    width: $pcWrapWidth;
  }

  .wrap-contents {
    margin: 0 auto;
    width: $pcCompareWrapWidth;
  }

  h1 {
    padding: 32px 0 0 0;
  }

  .compare-info ul li p {
    margin-bottom: 10px;
  }

  .request-other {
    border-bottom: none;
    float: right;
    display: block;

    li {
      border-radius: 4px;
      width: 334px;
      box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.16);

      &:first-of-type {
        margin: 0 0 19px;

        &::after {
          content: none;
        }
      }

      a {
        border-radius: 4px;
        color: $colorBlue;
        font-size: px(18);
      }

      &.house a {
        &.sp {
          display: none;
        }

        &.pc {
          display: block;
        }
      }

      &.lease a {
        padding: 8px 42px 9px 52px;
        background: url(~/assets/images/compare/icon_lease.png) no-repeat 20px
            0.6em,
          $colorSubButton url(~/assets/images/compare/arrow_link_pc.png)
            no-repeat 294px center;
        background-size: 24px 22px, 20px 20px;
      }

      &.house a {
        padding: 8px 42px 9px 52px;
        background: url(~/assets/images/compare/icon_house.png) no-repeat 20px
            0.6em,
          $colorSubButton url(~/assets/images/compare/arrow_link_pc.png)
            no-repeat 294px center;
        background-size: 24px 23px, 20px 20px;
      }

      &.lease a:hover {
        background: url(~/assets/images/compare/icon_lease.png) no-repeat 20px
            0.6em,
          #efefef url(~/assets/images/compare/arrow_link_pc.png) no-repeat
            294px center;
        background-size: 24px 22px, 20px 20px;
      }

      &.house a:hover {
        background: url(~/assets/images/compare/icon_house.png) no-repeat 20px
            0.6em,
          #efefef url(~/assets/images/compare/arrow_link_pc.png) no-repeat
            294px center;
        background-size: 24px 23px, 20px 20px;
      }
    }
  }

  .commonGuide {
    margin: 0;
    padding: 13px;
    width: 100%;
    float: left;
    box-sizing: border-box;
    p {
      padding: 6px 0 0;
      font-size: px(11);
      br {
        display: block;
      }
    }
  }

  .commonGuide-title {
    padding: 0 0 3px;

    div {
      font-size: px(15);
    }

    span {
      font-size: px(11);
    }
  }
  .guideCalculation {
    padding: 30px 0 80px;
  }

  :deep {
    .guideCalculation-inner {
      padding: 13px;
    }

    .guideCalculation-title {
      padding: 0 0 3px;
      font-size: px(15);
    }

    .guideCalculation-inner {
      ul {
        padding: 6px 0 0;
      }

      li {
        font-size: px(11);
      }
    }

    .guideCalculation-end {
      padding: 16px 0 0;
      font-size: px(15);
      text-align: right;
    }
  }

  .documentNumber {
    padding: 40px 0 0;
  }

  .pageTop {
    @include pageTop(pc);
  }
}
</style>
